import React from 'react';
import { Card, Typography } from "@material-tailwind/react";
import { FiDollarSign, FiActivity, FiTrendingUp, FiUsers } from 'react-icons/fi';

const SummaryCards = ({ matrixData, summaryData, loading = false }) => {
    if (loading) {
        return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {[...Array(4)].map((_, i) => (
                    <Card key={i} className="p-4">
                        <div className="animate-pulse space-y-3">
                            <div className="h-8 w-8 bg-gray-200 rounded-lg"></div>
                            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                            <div className="h-6 bg-gray-200 rounded w-1/2"></div>
                        </div>
                    </Card>
                ))}
            </div>
        );
    }

    const formatCurrency = (value) => {
        if (!value || value === 0) return 'KES 0';
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    };

    // Calculate metrics from data
    const calculateMetrics = () => {
        let totalRevenue = 0;
        let totalTransactions = 0;
        let activeCars = 0;
        let transactionTypes = 0;

        // Calculate WAR minus WRR for total revenue
        if (summaryData && summaryData.length > 0) {
            let warTotal = 0;
            let wrrTotal = 0;

            summaryData.forEach(item => {
                const transType = item.trans_type?.toUpperCase();
                const netAmount = parseFloat(item.net_amount || 0);

                if (transType === 'WAR') {
                    warTotal += netAmount;
                } else if (transType === 'WRR') {
                    wrrTotal += netAmount;
                }
            });

            totalRevenue = warTotal - wrrTotal;
            totalTransactions = summaryData.reduce((sum, item) => sum + parseInt(item.transaction_count || 0), 0);
            transactionTypes = summaryData.length;
        }

        // Get active cars count from matrix data
        if (matrixData && matrixData.cars) {
            activeCars = matrixData.cars.length;
        }

        const avgRevenuePerCar = activeCars > 0 ? totalRevenue / activeCars : 0;

        return {
            totalRevenue,
            totalTransactions,
            activeCars,
            transactionTypes,
            avgRevenuePerCar
        };
    };

    const metrics = calculateMetrics();

    const getValueColor = (value) => {
        if (value > 0) return 'text-green-600';
        if (value < 0) return 'text-red-600';
        return 'text-gray-600';
    };

    const cards = [
        {
            title: 'Total Revenue',
            value: formatCurrency(metrics.totalRevenue),
            icon: <FiDollarSign className="w-6 h-6" />,
            color: 'bg-green-50 text-green-600',
            valueColor: getValueColor(metrics.totalRevenue)
        },
        {
            title: 'Total Transactions',
            value: metrics.totalTransactions.toLocaleString(),
            icon: <FiActivity className="w-6 h-6" />,
            color: 'bg-blue-50 text-blue-600',
            valueColor: 'text-blue-gray-800'
        },
        {
            title: 'Active Cars',
            value: metrics.activeCars.toString(),
            icon: <FiUsers className="w-6 h-6" />,
            color: 'bg-purple-50 text-purple-600',
            valueColor: 'text-blue-gray-800'
        },
        {
            title: 'Avg per Car',
            value: formatCurrency(metrics.avgRevenuePerCar),
            icon: <FiTrendingUp className="w-6 h-6" />,
            color: 'bg-orange-50 text-orange-600',
            valueColor: getValueColor(metrics.avgRevenuePerCar)
        }
    ];

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {cards.map((card, index) => (
                <Card key={index} className="p-4 hover:shadow-lg transition-shadow">
                    <div className="flex items-center justify-between">
                        <div>
                            <Typography variant="small" color="blue-gray" className="font-medium">
                                {card.title}
                            </Typography>
                            <Typography variant="h5" className={`font-bold mt-1 ${card.valueColor}`}>
                                {card.value}
                            </Typography>
                        </div>
                        <div className={`p-2 rounded-lg ${card.color}`}>
                            {card.icon}
                        </div>
                    </div>
                </Card>
            ))}
        </div>
    );
};

export default SummaryCards;
